import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  extractErrorMessage,
  TASK_COMMENT_MAX_LENGTH,
  useCreateTaskComment,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { z } from 'zod';

export enum TaskNewCommentField {
  Text = 'text',
}

export const COMMENT_TEXTAREA_ROWS = 6 as const;

export const useTaskNewCommentValidationSchema = () => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      z.object({
        [TaskNewCommentField.Text]: z
          .string()
          .transform(value => value.trim())
          .pipe(
            z
              .string()
              .min(1, {
                message: t('errors.validation.required', {
                  fieldLabel: t('components.taskDetails.comments.yourComment'),
                }),
              })
              .max(TASK_COMMENT_MAX_LENGTH, {
                message: t('errors.validation.cantBeLonger', {
                  fieldLabel: t('components.taskDetails.comments.yourComment'),
                  maxLength: TASK_COMMENT_MAX_LENGTH,
                }),
              })
          ),
      }),
    [t]
  );
};

export type TaskNewCommentFormValues = z.infer<
  ReturnType<typeof useTaskNewCommentValidationSchema>
>;

export type TaskNewCommentFormInputValues = z.input<
  ReturnType<typeof useTaskNewCommentValidationSchema>
>;

const getDefaultValues = (): TaskNewCommentFormInputValues => ({
  [TaskNewCommentField.Text]: '',
});

interface UseTaskNewCommentFormParams {
  taskId: string;
  onSuccess: () => void;
}

export const useTaskNewCommentForm = ({
  taskId,
  onSuccess,
}: UseTaskNewCommentFormParams) => {
  const { t } = useTranslation();
  const { open } = useNotification();
  const validationSchema = useTaskNewCommentValidationSchema();

  const defaultValues = useMemo(() => getDefaultValues(), []);

  const formProps = useForm<
    TaskNewCommentFormInputValues,
    null,
    TaskNewCommentFormValues
  >({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const { reset } = formProps;

  const { mutateAsync: createComment, isPending } = useCreateTaskComment(
    taskId,
    {
      onSuccess: () => {
        open({
          type: 'success',
          message: t('notifications.create.success', {
            entity: t('entity.taskComment'),
          }),
        });
        reset(defaultValues);
        onSuccess();
      },
      onError: (error: Error) => {
        const errorMessage = extractErrorMessage(error);
        open({
          type: 'error',
          message:
            errorMessage ||
            t('notifications.create.failure', {
              entity: t('entity.taskComment'),
            }),
        });
      },
    }
  );

  const onSubmit = async (values: TaskNewCommentFormValues) => {
    await createComment({
      comment: values[TaskNewCommentField.Text],
    });
  };

  return {
    formProps,
    onSubmit,
    isPending,
    isValid: formProps.formState.isValid,
  };
};
